import React from 'react';
import { FileText, Phone, Mail } from 'lucide-react';
import { Card } from '../components/ui/card';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50">
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-teal-500 rounded-2xl mb-6 shadow-lg">
              <FileText className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Terms of Service
            </h1>
            <p className="text-gray-600">Last Updated: March 2025</p>
          </div>

          <Card className="p-8 lg:p-12 bg-white shadow-xl border-gray-100">
            <div className="prose prose-lg max-w-none">
              <Section title="1. Acceptance of Terms">
                <p>
                  By accessing or using PriEdu ("Platform"), you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing this Platform.
                </p>
              </Section>

              <Section title="2. Description of Service">
                <p>PriEdu provides the following services:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Tutor Marketplace:</strong> A platform connecting students/parents with verified home tutors for personalized learning</li>
                  <li><strong>Free Quiz-Based Tests:</strong> Comprehensive quiz tests available at no cost to all registered users</li>
                  <li><strong>Demo Class Booking:</strong> Free demo sessions with tutors before committing to regular classes</li>
                  <li><strong>Future Services:</strong> Mobile application for study material delivery, expanded test series, and enhanced learning features (coming soon)</li>
                </ul>
              </Section>

              <Section title="3. User Accounts">
                <p><strong>Student/Parent Accounts:</strong></p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                  <li>Must provide accurate personal and contact information</li>
                  <li>Responsible for maintaining account security</li>
                  <li>May book demo classes and access free quizzes</li>
                  <li>Must be 18+ or have parental/guardian consent</li>
                </ul>
                <p><strong>Tutor Accounts:</strong></p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Must provide accurate qualifications and experience</li>
                  <li>Subject to verification process before activation</li>
                  <li>Responsible for maintaining updated profile information</li>
                  <li>Must comply with our code of conduct</li>
                </ul>
              </Section>

              <Section title="4. Tutor Verification">
                <p>
                  All tutors on our platform undergo a rigorous verification process including:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Identity verification through government-issued documents</li>
                  <li>Educational qualification verification</li>
                  <li>Background checks where applicable</li>
                  <li>Skill assessment tests</li>
                </ul>
                <p>
                  PriEdu reserves the right to reject or remove any tutor who fails verification or violates our standards.
                </p>
              </Section>

              <Section title="5. Booking and Classes">
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Demo classes are free and have no obligation to continue</li>
                  <li>Regular class schedules and fees are agreed upon between tutor and student/parent</li>
                  <li>PriEdu facilitates connections but is not responsible for the quality of individual tutoring sessions</li>
                  <li>Cancellations should be communicated in advance</li>
                </ul>
              </Section>

              <Section title="6. Free Quiz Usage">
                <div className="bg-green-50 p-4 rounded-lg border border-green-200 mb-4">
                  <p className="text-green-800 font-medium">
                    Quiz-based tests are currently FREE for all registered users!
                  </p>
                </div>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Access to quizzes across multiple subjects and classes</li>
                  <li>Progress tracking and performance analytics</li>
                  <li>No limit on quiz attempts during the free period</li>
                  <li>PriEdu reserves the right to introduce premium features in the future with advance notice</li>
                </ul>
              </Section>

              <Section title="7. Payments">
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Tutor fees are set by individual tutors and displayed on their profiles</li>
                  <li>Payment terms are agreed upon directly between tutors and students/parents</li>
                  <li>PriEdu may charge a platform fee from tutors for successful connections</li>
                  <li>Refund policies are governed by individual tutor agreements</li>
                </ul>
              </Section>

              <Section title="8. Intellectual Property">
                <p>
                  All content on the Platform, including text, graphics, logos, quiz questions, and software, is the property of PriEdu or its content suppliers and is protected by intellectual property laws.
                </p>
                <p>
                  Users may not copy, reproduce, distribute, or create derivative works without explicit permission.
                </p>
              </Section>

              <Section title="9. User Conduct">
                <p>Users agree NOT to:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Provide false or misleading information</li>
                  <li>Impersonate another person or entity</li>
                  <li>Use the Platform for any illegal purpose</li>
                  <li>Harass, abuse, or harm other users</li>
                  <li>Attempt to circumvent security measures</li>
                  <li>Share quiz content outside the Platform</li>
                </ul>
              </Section>

              <Section title="10. Limitation of Liability">
                <p>
                  PriEdu acts as a platform connecting students with tutors. We are not responsible for:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>The quality, effectiveness, or outcome of tutoring sessions</li>
                  <li>Disputes between tutors and students/parents</li>
                  <li>Any direct, indirect, incidental, or consequential damages arising from Platform use</li>
                </ul>
              </Section>

              <Section title="11. Termination">
                <p>
                  PriEdu reserves the right to terminate or suspend accounts that violate these Terms of Service or for any other reason at our discretion. Users may also delete their accounts at any time.
                </p>
              </Section>

              <Section title="12. Changes to Terms">
                <p>
                  We may modify these Terms of Service at any time. Users will be notified of significant changes via email or Platform notification. Continued use of the Platform after changes constitutes acceptance of the new terms.
                </p>
              </Section>

              <Section title="13. Governing Law">
                <p>
                  These Terms of Service are governed by and construed in accordance with the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in Uttar Pradesh, India.
                </p>
              </Section>

              <Section title="14. Contact Us">
                <p>
                  For questions about these Terms of Service, please contact us:
                </p>
                <div className="bg-blue-50 p-6 rounded-xl mt-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Phone className="h-5 w-5 text-blue-600" />
                      <span className="font-medium">94555 45273</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5 text-blue-600" />
                      <span className="font-medium">support@priedu.com</span>
                    </div>
                  </div>
                </div>
              </Section>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
      {title}
    </h2>
    <div className="text-gray-700 leading-relaxed space-y-3">
      {children}
    </div>
  </div>
);

export default TermsOfService;
